<template>
  <w-modal
    :model-value="isOpen"
    @update:model-value="(val) => !val && emit('close')"
    size="small"
    class="device-selection-modal"
    :title="$t('deviceSelection.title', 'Iniciar nova gravação Noa Notes')"
    :close-on-click-outside="false"
    style="z-index: 999999999"
    @close="emit('close')"
  >
    <template #body>
      <div class="device-selection-content">
        <w-stack class="welcome-container" gap="small" direction="column">
          <w-text type="body">
            {{ $t('deviceSelection.subtitle', 'Noa Notes é uma transcrição de IA, use-a para automatizar todas as suas anotações durante a visita.') }}
          </w-text>
          
          <w-spacer size="m" />
          
          <div class="terms-text">
            <w-text type="caption" class="muted-text">
              {{ $t('deviceSelection.termsTextStart', 'Ao iniciar a transcrição, você concorda com nossos') }}
              <w-link target="_blank" :href="termsUrl">{{ $t('deviceSelection.termsTextLinkText', 'Termos e Condições') }}</w-link>.
              {{ $t('deviceSelection.termsTextEnd', 'Peça permissão ao seu paciente antes de começar a transcrever o áudio.') }}
            </w-text>
          </div>
        </w-stack>

      </div>
    </template>

    <template #footer>
      <div class="button-container">
        <!-- Mobile button with QR code popover -->
        <w-popover position="top" class="qr-popover" style="z-index: 9999999999">
          <w-button
            class="device-button"
            :label="$t('deviceSelection.continueOnMobile', 'Continuar no celular')"
            variant="secondary"
            leading-icon="mobile-phone"
            @click="onClickMobile"
            :loading="!transcriptionUrl"
            :disabled="!transcriptionUrl"
          />

          <template #content>
            <div class="qr-popover-content">
              <w-text type="caption" text-align="center">
                {{ $t('deviceSelection.scanQrCode', 'Escaneie o código QR para gravar no celular') }}
              </w-text>
              
              <w-spacer size="s" />
              
              <div class="qr-code-box">
                <w-spinner v-if="!transcriptionQrCode && qrCodeState === 'pending'" size="medium" />
                <w-icon v-else-if="qrCodeState === 'error'" name="warning" color="error" size="medium" />
                <!-- data-dd-privacy="hidden" prevents the QR code (which encodes a session
                     token) from being captured when Datadog Session Replay is enabled. -->
                <img
                  v-else-if="transcriptionQrCode"
                  :src="transcriptionQrCode"
                  class="qr-code-img"
                  alt="QR Code"
                  data-dd-privacy="hidden"
                />
              </div>
            </div>
          </template>
        </w-popover>

        <w-button
          class="device-button"
          @click="onClickDesktop"
          :label="$t('deviceSelection.startRecording', 'Iniciar gravação')"
          variant="primary"
          trailing-icon="arrow-right"
          :loading="!transcriptionUrl"
          :disabled="!transcriptionUrl"
        />
      </div>
    </template>
  </w-modal>
</template>

<script setup lang="ts">
/**
 * AIAssistantDeviceSelectionModal.vue - Device Selection Modal
 * 
 * Standalone modal for selecting recording device (desktop or mobile).
 * Shown after template selection, before the recorder modal opens.
 */
import { ref, watch, onMounted } from "vue";
import { UserTracking } from "@/utils/UserTracking";

const debugLog = (location: string, message: string, data?: Record<string, unknown>) => {
  console.log(`[DeviceSelectionModal:${location}] ${message}`, data || "");
};

const termsUrl = "https://cdn.feegow.com/terms/Termos-e-Condicoes-Gerais-de-Uso-Noa-BR-PT.pdf";

// =============================================================================
// Props & Emits
// =============================================================================

interface Props {
  isOpen: boolean;
  sessionData: {
    recorderUrl?: string;
    qrCode?: string;
    [key: string]: unknown;
  } | null;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "select-desktop", url: string): void;
  (e: "select-mobile"): void;
}>();

// =============================================================================
// State
// =============================================================================

const transcriptionQrCode = ref<string | null>(null);
const transcriptionUrl = ref<string | null>(null);
const qrCodeState = ref<"pending" | "error" | "created">("pending");

// =============================================================================
// Watchers
// =============================================================================

watch(
  () => props.sessionData,
  (newData) => {
    if (newData) {
      debugLog("watch:sessionData", "Session data received", {
        hasRecorderUrl: !!newData.recorderUrl,
        hasQrCode: !!newData.qrCode,
      });
      
      transcriptionUrl.value = newData.recorderUrl || null;
      transcriptionQrCode.value = newData.qrCode || null;
      qrCodeState.value = newData.qrCode ? "created" : "pending";
    }
  },
  { immediate: true }
);

watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      debugLog("watch:isOpen", "Modal opened", {
        hasSessionData: !!props.sessionData,
        hasRecorderUrl: !!props.sessionData?.recorderUrl,
      });
    }
  }
);

// =============================================================================
// Event Handlers
// =============================================================================

const onClickDesktop = () => {
  debugLog("onClickDesktop", "User selected desktop recording", {
    hasUrl: !!transcriptionUrl.value,
  });
  
  UserTracking.trackEvent("noa_notes_recording_device_selected", {
    action: "initiate noa notes record",
    device_type: "desktop",
    noa_notes_client: "feegow",
    page_category: "patients",
    page_section: "patient profile",
    page_type: "",
  });

  if (transcriptionUrl.value) {
    emit("select-desktop", transcriptionUrl.value);
  }
};

const onClickMobile = () => {
  debugLog("onClickMobile", "User selected mobile recording - QR popover shown");
  
  UserTracking.trackEvent("noa_notes_recording_device_selected", {
    action: "initiate noa notes record",
    device_type: "mobile",
    noa_notes_client: "feegow",
    page_category: "patients",
    page_section: "patient profile",
    page_type: "",
  });

  // QR code popover is handled by w-popover component automatically
  emit("select-mobile");
};

// =============================================================================
// Lifecycle
// =============================================================================

onMounted(() => {
  debugLog("onMounted", "Component mounted", {
    isOpen: props.isOpen,
    hasSessionData: !!props.sessionData,
  });
});
</script>

<style scoped lang="scss">
.device-selection-content {
  padding: 0;
}

.welcome-container {
  text-align: left;
}

.terms-text {
  text-align: left;
}

.muted-text {
  color: var(--w-text-color-tertiary, #6b7280);
  font-size: 11px;
  line-height: 1.4;
  opacity: 0.8;
}

.button-container {
  display: flex;
  flex-direction: row;
  gap: var(--w-space-s);
  justify-content: flex-end;
  
  .device-button {
    min-width: auto;
  }
}

.qr-popover-content {
  padding: var(--w-space-s);
  text-align: center;
  width: 160px;
}

.qr-code-box {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 140px;
  background: #fff;
  border-radius: var(--w-border-radius-s);
}

.qr-code-img {
  width: 130px;
  height: 130px;
}
</style>

<style>
/* Modal positioning override */
.device-selection-modal .w-modal-container {
  max-width: 480px !important;
}

/* Ensure QR popover renders above the modal */
.qr-popover .w-popover-popout,
.w-popover-popout {
  z-index: 9999999999 !important;
}
</style>

